/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, Image } from 'antd-v5';
import { LoadingOutlined } from '@ant-design/icons';
import './styles.scss';
import { isUrlImageValid } from '../../../../../utils/checkImage';

const NOT_FOUND_IMAGE = 'https://dashboard-v2-images.s3.amazonaws.com/not-found.png';

const ProductsV2Card = props => {
  const {
    id,
    image,
    images,
    title,
    description,
    onClick,
    isSelected,
    hasImage,
    isClientPage,
    selectedProducts,
    toggleProductSelection,
    selectedAndDisabledProducts,
  } = props;

  const [hasImageValid, setHasImageValid] = useState(true);
  const [loading, setLoading] = useState(true);
  const [displayImage, setDisplayImage] = useState(image);
  const cancelledRef = useRef(false);
  const failedUrlsRef = useRef(new Set());

  const tryFindWorkingImage = useCallback(
    async (skipUrl) => {
      if (!images?.length) return false;
      for (const img of images) {
        const url = img.url || img;
        if (url && url !== skipUrl && !failedUrlsRef.current.has(url)) {
          const valid = await isUrlImageValid(url);
          if (cancelledRef.current) return false;
          if (valid) {
            setDisplayImage(url);
            setHasImageValid(true);
            return true;
          }
          failedUrlsRef.current.add(url);
        }
      }
      return false;
    },
    [images],
  );

  useEffect(() => {
    cancelledRef.current = false;
    failedUrlsRef.current = new Set();
    setDisplayImage(image);
    setHasImageValid(true);
    setLoading(true);

    if (image) {
      isUrlImageValid(image).then(async isValid => {
        if (cancelledRef.current) return;
        if (!isValid) {
          failedUrlsRef.current.add(image);
          const found = await tryFindWorkingImage(image);
          if (cancelledRef.current) return;
          if (!found) setHasImageValid(false);
        } else {
          setHasImageValid(true);
        }
      });
    }

    return () => {
      cancelledRef.current = true;
    };
  }, [image, images, tryFindWorkingImage]);

  const handleImageError = useCallback(() => {
    failedUrlsRef.current.add(displayImage);
    tryFindWorkingImage(displayImage).then(found => {
      if (cancelledRef.current) return;
      if (!found) setHasImageValid(false);
    });
  }, [displayImage, tryFindWorkingImage]);

  const handleSelect = useCallback(
    e => {
      console.log({ select: e });
      e.stopPropagation();
      e.preventDefault();
      if (isClientPage) {
        toggleProductSelection(id);
      }
    },
    [isClientPage],
  );

  return hasImageValid || !hasImage ? (
    <div
      className={`products-v2-card${isSelected ? ' products-v2-card--selected' : ''}`}
      onClick={e => {
        console.log({ e });
        onClick();
      }}
    >
      {!!isClientPage && (
        <Checkbox
          checked={selectedProducts?.has(id)}
          onChange={handleSelect}
          disabled={selectedAndDisabledProducts?.has(id)}
        />
      )}
      <div className="products-v2-card__image">
        <Image
          src={displayImage || NOT_FOUND_IMAGE}
          fallback={NOT_FOUND_IMAGE}
          preview={false}
          alt="product"
          onLoad={() => setLoading(false)}
          onError={handleImageError}
          width="40px"
          height="40px"
        />
      </div>
      <div className="products-v2-card__body">
        {loading && !!hasImage ? (
          <LoadingOutlined style={{ color: '#33aafc' }} />
        ) : (
          <>
            <span className="products-v2-card__title line-clamp-1">{title}</span>
            <span className="products-v2-card__description line-clamp-1">{description}</span>
          </>
        )}
      </div>
    </div>
  ) : null;
};

export default ProductsV2Card;
